import { animate } from 'https://cdn.jsdelivr.net/npm/motion@12.38.0/+esm';

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

class DaybreakFaq extends HTMLElement {
  #controller = new AbortController();
  #motion = {
    type: 'spring',
    stiffness: 320,
    damping: 34,
    mass: 0.8,
  };

  connectedCallback() {
    this.querySelectorAll('details').forEach((details) => {
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.daybreak-faq__answer-clip');

      if (!(summary instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

      summary.addEventListener('click', this.#handleSummaryClick, {
        signal: this.#controller.signal,
      });

      this.#setInitialPanelState(details, panel);
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleSummaryClick = (event) => {
    const summary = event.currentTarget;
    if (!(summary instanceof HTMLElement)) return;

    const details = summary.closest('details');
    if (!(details instanceof HTMLDetailsElement)) return;

    event.preventDefault();

    if (details.dataset.animating === 'true') return;

    if (details.open) {
      this.#close(details);
    } else {
      this.#open(details);
    }
  };

  #setInitialPanelState(details, panel) {
    if (details.open) {
      panel.style.height = 'auto';
      panel.style.opacity = '1';
    } else {
      panel.style.height = '0px';
      panel.style.opacity = '0';
    }
  }

  async #open(details) {
    const panel = details.querySelector('.daybreak-faq__answer-clip');
    const answer = details.querySelector('.daybreak-faq__answer');
    const icon = details.querySelector('.daybreak-faq__icon img');

    if (!(panel instanceof HTMLElement)) return;

    details.dataset.animating = 'true';
    details.open = true;
    panel.style.overflow = 'hidden';
    panel.style.height = '0px';
    panel.style.opacity = '0';

    if (answer instanceof HTMLElement) {
      answer.style.transform = 'translateY(-4px)';
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (reducedMotionQuery.matches) {
      panel.style.height = 'auto';
      panel.style.opacity = '1';
      if (answer instanceof HTMLElement) answer.style.transform = '';
      if (icon instanceof HTMLElement) icon.style.transform = 'rotate(180deg)';
      delete details.dataset.animating;
      return;
    }

    const targetHeight = panel.scrollHeight;
    const animations = [
      animate(panel, { height: [`0px`, `${targetHeight}px`], opacity: [0, 1] }, this.#motion),
    ];

    if (answer instanceof HTMLElement) {
      animations.push(animate(answer, { transform: ['translateY(-4px)', 'translateY(0px)'] }, this.#motion));
    }

    if (icon instanceof HTMLElement) {
      animations.push(animate(icon, { transform: ['rotate(0deg)', 'rotate(180deg)'] }, this.#motion));
    }

    await this.#settle(animations);

    panel.style.height = 'auto';
    panel.style.opacity = '1';
    if (answer instanceof HTMLElement) answer.style.transform = '';
    if (icon instanceof HTMLElement) icon.style.transform = 'rotate(180deg)';
    delete details.dataset.animating;
  }

  async #close(details) {
    const panel = details.querySelector('.daybreak-faq__answer-clip');
    const answer = details.querySelector('.daybreak-faq__answer');
    const icon = details.querySelector('.daybreak-faq__icon img');

    if (!(panel instanceof HTMLElement)) return;

    details.dataset.animating = 'true';
    panel.style.overflow = 'hidden';
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.opacity = '1';

    if (reducedMotionQuery.matches) {
      details.open = false;
      panel.style.height = '0px';
      panel.style.opacity = '0';
      if (answer instanceof HTMLElement) answer.style.transform = '';
      if (icon instanceof HTMLElement) icon.style.transform = '';
      delete details.dataset.animating;
      return;
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    const animations = [animate(panel, { height: [`${panel.scrollHeight}px`, '0px'], opacity: [1, 0] }, this.#motion)];

    if (answer instanceof HTMLElement) {
      animations.push(animate(answer, { transform: ['translateY(0px)', 'translateY(-4px)'] }, this.#motion));
    }

    if (icon instanceof HTMLElement) {
      animations.push(animate(icon, { transform: ['rotate(180deg)', 'rotate(0deg)'] }, this.#motion));
    }

    await this.#settle(animations);

    details.open = false;
    panel.style.height = '0px';
    panel.style.opacity = '0';
    if (answer instanceof HTMLElement) answer.style.transform = '';
    if (icon instanceof HTMLElement) icon.style.transform = '';
    delete details.dataset.animating;
  }

  async #settle(animations) {
    await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
  }
}

if (!customElements.get('daybreak-faq')) {
  customElements.define('daybreak-faq', DaybreakFaq);
}
